import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { Kicker } from "@/components/primitives";
import { apiClient, useAuth, formatApiError } from "@/lib/auth";
import { ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "bloomkeeper.token";

export default function AdminLogin() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: user?.email || "", password: "", admin_password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user?.is_admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.email || !form.password || !form.admin_password) {
      setErr("All three fields are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/login", form);
      localStorage.setItem(TOKEN_KEY, data.token);
      await refreshMe();
      toast.success("Admin unlocked");
      nav("/admin", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-8" data-testid="admin-login-page">
      <div className="h-card w-full max-w-md px-6 sm:px-10 py-10 shadow-seal animate-bloom-in">
        <Link to="/home" className="chip mb-4" data-testid="admin-login-back"><ArrowLeft size={14} /> Back</Link>
        <div className="flex justify-center mb-4"><BrandLogo size={84} /></div>
        <Kicker>Heritage Office</Kicker>
        <h1 className="font-display text-4xl text-sage-dark text-center mt-3">Admin unlock</h1>
        <p className="text-center font-display italic text-ink-soft mt-2">
          Sign in again with your keeper credentials plus the admin pass-phrase.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="admin-login-form">
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Email</span>
            <input className="field" type="email" autoComplete="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-login-email" required />
          </label>
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Account password</span>
            <input className="field" type="password" autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-login-password" required />
          </label>
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Admin pass-phrase</span>
            <input className="field" type="password"
              value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
              data-testid="admin-login-admin-password" required />
          </label>
          {err && (
            <div className="bg-cream-deep border border-heritage-border rounded-xl px-4 py-3 text-sm text-terracotta-dark" data-testid="admin-login-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-heritage w-full" data-testid="admin-login-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {busy ? "Unlocking..." : "Unlock the Office"}
          </button>
        </form>
      </div>
    </div>
  );
}
